import React, { useState, useRef, useEffect, useId, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faCheck, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useResolvedTheme, type ResolvedTheme } from './useResolvedTheme';
import './GhostSelect.css';

export interface SelectOption {
  value: string;
  label: React.ReactNode;
  textLabel?: string;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
}

interface GhostSelectProps {
  options: SelectOption[];
  value: string | string[];
  // ESLint's base rule treats TypeScript call-signature parameters as runtime variables.
  // eslint-disable-next-line no-unused-vars
  onChange: (value: any) => void;
  placeholder?: string;
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
  multiple?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: ResolvedTheme;
}

export const GhostSelect: React.FC<GhostSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option...',
  className = '',
  ariaLabel,
  disabled = false,
  multiple = false,
  searchable = false,
  searchPlaceholder = 'Buscar...',
  size = 'md',
  variant,
}) => {
  const resolvedTheme = useResolvedTheme(variant);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const id = useId();
  const selectRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);
  const pendingFocus = useRef<'search' | 'option' | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const updatePosition = () => {
    if (selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const estimatedDropdownHeight = 280; // max-h-64 + padding

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      let styleObj: React.CSSProperties = {
        left: `${rect.left}px`,
        width: `${rect.width}px`,
      };

      // If not enough space below, and more space above, open upwards
      if (spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow) {
        styleObj.bottom = `${viewportHeight - rect.top + 8}px`;
        styleObj.transformOrigin = 'bottom';
      } else {
        styleObj.top = `${rect.bottom + 8}px`;
        styleObj.transformOrigin = 'top';
      }

      setDropdownStyle(styleObj);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current && !selectRef.current.contains(event.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = (event: Event) => {
      if (isOpen) {
        if (dropdownRef.current && dropdownRef.current.contains(event.target as Node)) {
          return; // Allow scrolling inside the dropdown itself
        }
        setIsOpen(false);
      }
    };

    if (isOpen) {
      updatePosition();
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true); // Close on external scroll
      window.addEventListener('resize', handleScroll);
    } else {
      setSearchTerm('');
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen]);

  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  const closeMenu = (restoreFocus = false) => {
    pendingFocus.current = null;
    setIsOpen(false);
    if (restoreFocus) triggerRef.current?.focus({ preventScroll: true });
  };

  const isSelected = (optValue: string) => {
    if (multiple && Array.isArray(value)) {
      return value.includes(optValue);
    }
    return value === optValue;
  };

  const getTriggerLabel = () => {
    if (multiple && Array.isArray(value)) {
      if (value.length === 0) return placeholder;
      if (value.length === 1) {
        return options.find(o => o.value === value[0])?.label || placeholder;
      }
      return `${value.length} selected`;
    }
    const opt = options.find((o) => o.value === value);
    return opt ? opt.label : placeholder;
  };

  const handleOptionClick = (optValue: string) => {
    if (disabled) return;
    if (multiple) {
      const currentValues = Array.isArray(value) ? [...value] : [];
      if (currentValues.includes(optValue)) {
        onChange(currentValues.filter(v => v !== optValue));
      } else {
        onChange([...currentValues, optValue]);
      }
    } else {
      onChange(optValue);
      closeMenu(true);
    }
  };

  const hasSelection = multiple && Array.isArray(value) ? value.length > 0 : !!value;
  const singleSelectedOption = !multiple ? options.find(o => o.value === value) : null;

  const getSearchableText = (opt: SelectOption) => {
    if (opt.textLabel) return opt.textLabel.toLowerCase();
    if (typeof opt.label === 'string') return opt.label.toLowerCase();
    return opt.value.toLowerCase();
  };

  const filteredOptions = searchable
    ? options.filter(opt => getSearchableText(opt).includes(searchTerm.toLowerCase()))
    : options;

  const openMenu = (edge?: 'first' | 'last') => {
    if (disabled) return;
    const selectedIndex = filteredOptions.findIndex(option => isSelected(option.value));
    setActiveIndex(edge === 'first' ? 0 : edge === 'last' ? Math.max(0, filteredOptions.length - 1) : Math.max(0, selectedIndex));
    pendingFocus.current = searchable && !edge ? 'search' : 'option';
    setIsOpen(true);
  };

  const focusOption = (index: number) => {
    const next = Math.max(0, Math.min(index, filteredOptions.length - 1));
    setActiveIndex(next);
    const option = optionRefs.current[next];
    option?.focus({ preventScroll: true });
    option?.scrollIntoView?.({ block: 'nearest' });
  };

  useLayoutEffect(() => {
    if (!isOpen || !pendingFocus.current) return;
    const target = pendingFocus.current === 'search' ? searchRef.current : optionRefs.current[activeIndex] ?? listboxRef.current;
    const focusOptionOnOpen = pendingFocus.current === 'option';
    pendingFocus.current = null;
    target?.focus({ preventScroll: true });
    if (focusOptionOnOpen) target?.scrollIntoView?.({ block: 'nearest' });
  }, [isOpen, activeIndex]);

  return (
    <div data-ui="ghost-select" data-ghost-select="true" data-theme={resolvedTheme} className={`relative ${className}`} ref={selectRef}
      onBlur={event => {
        const next = event.relatedTarget;
        if (next instanceof Node && (selectRef.current?.contains(next) || dropdownRef.current?.contains(next))) return;
        closeMenu();
      }}>
      {/* Trigger Button */}
      <button
        type="button"
        ref={triggerRef}
        id={`${id}-trigger`}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? id : undefined}
        onClick={() => isOpen ? closeMenu() : openMenu()}
        onKeyDown={event => {
          if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          openMenu(event.key === 'ArrowUp' || event.key === 'End' ? 'last' : 'first');
        }}
        className={`
          ghost-select-trigger group relative overflow-hidden w-full flex items-center justify-between
          ${size === 'sm' ? 'px-2.5 py-1.5 text-xs' : size === 'lg' ? 'px-5 py-4 text-base' : 'px-4 py-3 text-sm'}
          rounded-2xl backdrop-blur-xl border transition-all duration-300
          ${resolvedTheme === 'light'
            ? disabled
              ? 'cursor-not-allowed border-slate-200 bg-slate-100 opacity-50'
              : 'cursor-pointer border-slate-300 bg-white hover:border-blue-300 hover:bg-blue-50/50 hover:shadow-[inset_0_0_16px_rgba(255,255,255,0.9)]'
            : disabled
              ? 'opacity-50 cursor-not-allowed bg-white/5 border-white/5'
              : 'cursor-pointer bg-white/5 hover:bg-white/10 border-white/10 hover:border-white/20 hover:shadow-[inset_0_0_20px_rgba(255,255,255,0.25)]'}
          ${isOpen
            ? resolvedTheme === 'light'
              ? 'border-blue-300 bg-blue-50/60 ring-2 ring-blue-100'
              : 'bg-white/10 border-white/30 shadow-[inset_0_0_20px_rgba(255,255,255,0.3)] ring-2 ring-white/10'
            : ''}
        `}
      >

        <span className={`relative z-10 flex items-center gap-2 font-medium ${resolvedTheme === 'light' ? (hasSelection ? 'text-slate-900' : 'text-slate-500') : (hasSelection ? 'text-white' : 'text-gray-400')}`}>
          {singleSelectedOption?.icon && <span>{singleSelectedOption.icon}</span>}
          {getTriggerLabel()}
        </span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`relative z-10 text-xs transition-transform duration-300 ${resolvedTheme === 'light' ? 'text-slate-500' : 'text-gray-400'} ${isOpen ? `rotate-180 ${resolvedTheme === 'light' ? 'text-blue-600' : 'text-white'}` : ''}`}
        />
      </button>

      {/* Dropdown Options (Portaled) */}
      {isOpen && typeof document !== 'undefined' && createPortal(
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          data-ui="ghost-select-dropdown"
          data-ghost-dropdown="true"
          data-theme={resolvedTheme}
          onKeyDown={event => {
            if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              closeMenu(true);
            } else if (event.key === 'Tab') {
              // Leave a portalled menu in the original trigger's tab order.
              closeMenu(true);
            }
          }}
          className={`
            fixed z-[99999] rounded-2xl overflow-hidden
            bg-black/60 backdrop-filter backdrop-blur-xl border border-white/20
            shadow-[0_8px_32px_rgba(0,0,0,0.8)] ring-1 ring-white/10
          `}
        >
          <div className="p-1.5 space-y-0.5 max-h-[280px] overflow-y-auto">
            {searchable && (
              <div className="ghost-select-search-area sticky top-0 z-20 mb-1 bg-black/60 backdrop-blur-xl p-1 rounded-xl">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faSearch} className="text-white/40 text-[11px]" />
                  </div>
                  <input
                    type="text"
                    ref={searchRef}
                    aria-label={searchPlaceholder}
                    aria-controls={id}
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setActiveIndex(0); }}
                    onKeyDown={event => {
                      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                        event.preventDefault();
                        focusOption(event.key === 'ArrowUp' ? filteredOptions.length - 1 : 0);
                      } else if (event.key === 'Enter') {
                        event.preventDefault();
                        const option = filteredOptions[activeIndex];
                        if (option) handleOptionClick(option.value);
                      }
                    }}
                    placeholder={searchPlaceholder}
                    className="ghost-select-search w-full bg-black/20 backdrop-blur-xl border border-white/20 text-white text-sm rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:ring-1 focus:ring-white/30 shadow-sm transition-all duration-300"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            )}
            <ul ref={listboxRef} id={id} role="listbox" aria-labelledby={`${id}-trigger`} aria-multiselectable={multiple || undefined} tabIndex={-1} className="space-y-0.5">
              {filteredOptions.map((option, index) => {
                const selected = isSelected(option.value);
                return (
                  <li
                    key={option.value}
                    ref={element => { optionRefs.current[index] = element; }}
                    role="option"
                    aria-selected={selected}
                    tabIndex={index === activeIndex ? 0 : -1}
                    data-ui={`ghost-select-option-${option.value}`}
                    data-ghost-select-option={option.value}
                    data-selected={selected ? 'true' : 'false'}
                    onFocus={() => setActiveIndex(index)}
                    onKeyDown={event => {
                      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Home' || event.key === 'End') {
                        event.preventDefault();
                        focusOption(event.key === 'Home' ? 0 : event.key === 'End' ? filteredOptions.length - 1 : index + (event.key === 'ArrowDown' ? 1 : -1));
                      } else if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleOptionClick(option.value);
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOptionClick(option.value);
                    }}
                    className={`
                      ghost-select-option relative overflow-hidden flex items-center justify-between px-3 py-2.5 rounded-xl cursor-pointer text-sm group transition-all duration-300
                      ${selected
                        ? 'bg-white/15 text-white font-medium shadow-[inset_0_0_15px_rgba(255,255,255,0.25)]'
                        : 'text-gray-300 hover:bg-white/10 hover:text-white hover:shadow-[inset_0_0_15px_rgba(255,255,255,0.25)]'}
                    `}
                  >

                    <div className="flex items-center gap-2 relative z-10">
                      {option.icon && (
                        <span className={`${selected ? 'text-white' : 'text-gray-400 group-hover:text-white'}`}>
                          {option.icon}
                        </span>
                      )}
                      <div className="flex flex-col">
                        <span>{option.label}</span>
                        {option.subtitle && (
                          <span className="text-[10px] text-gray-500 group-hover:text-gray-400 -mt-0.5">{option.subtitle}</span>
                        )}
                      </div>
                    </div>

                    {selected && (
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="text-white text-xs relative z-10"
                      />
                    )}

                  </li>
                );
              })}
            </ul>
            {filteredOptions.length === 0 && <div role="status" className="px-4 py-3 text-sm text-gray-500 text-center">No options available</div>}
          </div>
        </div>,
        selectRef.current?.closest('dialog') || document.body
      )}
    </div>
  );
};
